import { useState, useCallback } from "react";
import { bool, string, instanceOf, number } from "prop-types";
import { useActiveProjectStore, useActiveProjectsWebsocket } from "@hooks";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { changeProjectDeadlineQuery } from "@query";
import { Grid, IconButton } from "@material-ui/core";
import { ArrowBack } from "@assets";
import { format, set } from "date-fns";
import { Alert, Deadline, EstimateNameEditor, StatusBullet, ProjectActionMenu } from "@components";
import classnames from "classnames";
import useStyle from "./Header.style";
import { home } from "@paths";

export const Header = ({
  uuid,
  name,
  created,
  deadline,
  status,
  isMini,
  isOwner,
  isContractor,
  showActionMenu,
  description,
  isBusiness,
  id,
  projectBtSetUp,
  projectBtExported,
  hasComments,
}) => {
  const { t } = useTranslation();
  const activeProjects = useActiveProjectStore();
  const socket = useActiveProjectsWebsocket();
  const navigate = useNavigate();
  const classes = useStyle();

  const [alertText, setAlertText] = useState("");

  const canEdit = isOwner && !isContractor;

  const handleDateChange = useCallback(async (date) => {
    if (!uuid) return;
    try {
      const d = format(
        set(date, { hours: 0, minutes: 0, seconds: 0 }),
        "yyyy-MM-dd HH:mm:ss"
      );
      await changeProjectDeadlineQuery(uuid, d);
      activeProjects.editLocalProjectData(uuid, "dueOn", d);
    } catch (e) {
      setAlertText(e?.response?.data?.error || t("common.error"));
    }
  }, [uuid, activeProjects]);

  const handleNameChange = useCallback(async (newName) => {
    if (!uuid) return;
    await socket?.requestProjectRename(uuid, newName);
    activeProjects.editLocalProjectData(uuid, "name", newName);
  }, [uuid, socket, activeProjects]);

  return (
    <Grid
      item
      container
      direction="column"
      className={classes.headerContainer}
    >
      <Grid item container justifyContent="space-between">
        <EstimateNameEditor
          name={name}
          onSubmit={handleNameChange}
          allowEdition={canEdit}
          formClassName="-my-1-5"
        />
        {showActionMenu && (
          <Grid item>
            <ProjectActionMenu
              useCreateTemplate
              isOwner={isOwner}
              isContractor={isContractor}
              isMini={isMini}
              uuid={uuid}
              name={name}
              id={id}
              description={description}
              isBusiness={isBusiness}
              projectBtSetUp={projectBtSetUp}
              projectBtExported={projectBtExported}
              hasComments={hasComments}
            />
            <IconButton
              onClick={() => navigate(home)}
              color="primary"
              style={{ marginLeft: 8 }}
            >
              <ArrowBack />
            </IconButton>
          </Grid>
        )}
      </Grid>
      <Grid
        item
        container
        spacing={1}
        alignItems="center"
        style={{ marginTop: 4 }}
      >
        <Grid item>
          <Deadline
            date={deadline}
            status={status}
            onEdit={canEdit ? handleDateChange : undefined}
          />
          {isOwner && (
            <Alert
              title={alertText}
              isOpen={!!alertText}
              onAccept={() => setAlertText("")}
              acceptText={t("common.close")}
            />
          )}
        </Grid>
        <Grid
          item
          container
          alignItems="center"
          style={{ width: "max-content" }}
        >
          <StatusBullet className={classes.opaque} size="xs" />
        </Grid>
        <Grid item>
          <p className={classnames(classes.opaque, classes.text)}>
            {t(`project_types.${isMini ? "mini" : "standard"}`)}
          </p>
        </Grid>
        <Grid
          item
          container
          alignItems="center"
          style={{ width: "max-content" }}
        >
          <StatusBullet className={classes.opaque} size="xs" />
        </Grid>
        <Grid item>
          <p className={classnames(classes.opaque, classes.text)}>
            {`${t("common.created_on")} ${format(created, "dd MMM yyyy")}`}
          </p>
        </Grid>
      </Grid>
    </Grid>
  );
};

Header.propTypes = {
  id: number.isRequired,
  uuid: string.isRequired,
  isOwner: bool.isRequired,
  isContractor: bool,
  isMini: bool.isRequired,
  isBusiness: bool.isRequired,
  deadline: instanceOf(Date).isRequired,
  created: instanceOf(Date).isRequired,
  status: string.isRequired,
  description: string,
  name: string,
  showActionMenu: bool,
  projectBtSetUp: bool,
  projectBtExported: bool,
  hasComments: bool,
};
